import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  MapPin,
  Send,
  Linkedin,
  Twitter,
  Youtube,
  Clock,
  CheckCircle2,
  AlertCircle,
  Upload,
  FileText,
  X as XIcon,
  Paperclip,
} from 'lucide-react';
import {
  Container,
  Section,
  Badge,
  Card,
  Button,
  OtpModal,
  PhoneInput,
  MessageAssistToolbar,
  composePhoneNumber,
  DEFAULT_PHONE_VALUE,
  type PhoneValue,
} from '../components/ui';
import {
  PageHero,
  OurPurpose,
  WhyChooseUsSection,
  FormPrivacyNotice,
} from '../components/sections';
import { CONTACT_INFO, SOCIAL_LINKS } from '../constants';
import { sendOtp, validateOtpAndSubmit } from '../services/contactApi';
import {
  submitRfp,
  validateRfpDocument,
  MAX_RFP_BYTES,
  RFP_ACCEPT_ATTR,
} from '../services/rfpApi';
import { INDUSTRIES } from '../data/industries';
import visionCover from '../assets/istatec_cover_final.webm';

// Two modes for the contact-page form area. Both share input styling and
// the OTP-modal flow; they differ in field set + submit endpoint.
type FormMode = 'contact' | 'rfp';

// RFP mode pre-selects these two chips because the two most common intents
// for an RFP submission are prospect-side buying + partner-side selling.
// The user can toggle either chip off before submitting.
const DEFAULT_RFP_RELATIONSHIP_IDS = ['client-prospect', 'partner-alliance'];

interface ContactFormState {
  relationship: string;
  name: string;
  workEmail: string;
  personalEmail: string;
  company: string;
  phone: PhoneValue;
  service: string;
  customService: string;
  /**
   * Industry vertical the visitor operates in — selected from
   * `INDUSTRY_OPTIONS` below (which mirrors `INDUSTRIES` from
   * `src/data/industries.ts`). Optional on submit; empty falls back to
   * "NA" server-side.
   */
  industry: string;
  message: string;
}

const INITIAL_FORM: ContactFormState = {
  relationship: '',
  name: '',
  workEmail: '',
  personalEmail: '',
  company: '',
  phone: DEFAULT_PHONE_VALUE,
  service: '',
  customService: '',
  industry: '',
  message: '',
};

// Options for the "Service Interested In" dropdown. Kept in sync (by
// hand — no import needed) with the "What We Do" sub-items in the
// sidebar mega-menu (see `SIDEBAR_MENU` in
// `src/components/layout/SidebarDrawer.tsx`). Keeping the labels
// identical means the service the user hovers in the drawer is the
// same string that lands in the admin notification.
//
// "Explore our services" is intentionally omitted — that's a
// navigational entry, not a bookable service.
const SERVICE_OPTIONS = [
  { id: 'it-consultancy-augmentation', title: 'IT Consultancy & Augmentation' },
  { id: 'ai-genai-automations', title: 'AI / GenAI Automations' },
  { id: 'salesforce-automation', title: 'Salesforce Automation' },
  { id: 'cybersecurity-automation', title: 'Cybersecurity Automation' },
  { id: 'application-services', title: 'Application Services' },
  { id: 'artificial-intelligence', title: 'Artificial Intelligence' },
  { id: 'cloud-infrastructure-security', title: 'Cloud, Infrastructure, and Security' },
  { id: 'data-engineering-analytics', title: 'Data Engineering and Analytics' },
  { id: 'marketing-engagement', title: 'Marketing and Engagement' },
  { id: 'product-platforms', title: 'Product and Platforms' },
  { id: 'quality-intelligence', title: 'Quality Intelligence' },
] as const;

// Relationship dropdown — asked first so the admin knows the intent of the
// inquiry before diving into the specifics.
const RELATIONSHIP_OPTIONS = [
  { id: 'client-prospect', label: 'Client / Prospect' },
  { id: 'partner-alliance', label: 'Partner / Alliance' },
  { id: 'investor-analyst', label: 'Investor / Analyst' },
  { id: 'media-journalist', label: 'Media / Journalist' },
  { id: 'others', label: 'Others' },
] as const;

// Industry dropdown — derived from the shared `INDUSTRIES` catalog
// (`src/data/industries.ts`) so the sidebar mega-menu, the /industries
// listing page, the /industries/<slug> detail pages, and this contact
// form's options are all in lock-step. Adding an industry there
// automatically surfaces it here.
//
// `other-not-sure` is added for visitors whose vertical isn't listed
// (or who genuinely don't know) — matches the "Other / Not Sure" flow
// on the service dropdown below.
const INDUSTRY_OPTIONS = [
  ...INDUSTRIES.map((ind) => ({ id: ind.id, label: ind.title })),
  { id: 'other-not-sure', label: 'Other / Not Sure' },
] as const;

function resolveRelationshipLabel(state: ContactFormState): string {
  const match = RELATIONSHIP_OPTIONS.find((o) => o.id === state.relationship);
  return match?.label || state.relationship || 'NA';
}

function resolveServiceLabel(state: ContactFormState): string {
  if (state.service === 'other') {
    return state.customService.trim() || 'Other / Not Sure';
  }
  const listed = SERVICE_OPTIONS.find((s) => s.id === state.service);
  // Falling back to the raw id keeps admin emails legible even if
  // someone submits with an unknown value (e.g. because they had the
  // page open across a deploy that renamed an option).
  return listed?.title || state.service;
}

function resolveIndustryLabel(industryId: string): string {
  if (!industryId) return 'NA';
  const listed = INDUSTRY_OPTIONS.find((i) => i.id === industryId);
  return listed?.label || industryId;
}

export const Contact: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ContactFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // ---- Contact vs RFP form mode --------------------------------------------
  const [formMode, setFormMode] = useState<FormMode>('contact');
  // Direction used by the AnimatePresence variants:
  //   +1 → slide the incoming form in from the right (Contact → RFP)
  //   -1 → slide the incoming form in from the left  (RFP → Contact)
  // Tracked as a ref so a re-render triggered by AnimatePresence doesn't
  // race the state update we make from `handleModeSwitch`.
  const slideDirRef = useRef<1 | -1>(1);
  // `submittedMode` freezes the mode at the time of a successful submit,
  // so if the user switches tabs on the success screen we still know which
  // flow they just completed.
  const [submittedMode, setSubmittedMode] = useState<FormMode>('contact');

  // ---- RFP-only field state ------------------------------------------------
  // Relationship in RFP mode is a **multi-select** (chips), unlike the
  // single-select dropdown used by the contact form. We store the list of
  // selected option ids and resolve them to labels at submit time.
  const [rfpRelationships, setRfpRelationships] = useState<string[]>([
    ...DEFAULT_RFP_RELATIONSHIP_IDS,
  ]);
  const [rfpFile, setRfpFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // ---- Form-swap height stability ------------------------------------------
  // The two tabs (Contact us / Submit an RFP) render forms of slightly
  // different heights. Left to itself the surrounding purple section
  // would grow / shrink every time the user toggles, which reads as
  // jitter and pushes the "Our Purpose" section up and down.
  //
  // We pin the outer form wrapper to the **maximum** height we've ever
  // observed from either form via a ResizeObserver. Grow-only — never
  // shrinks — so once the taller (RFP) form has been rendered once,
  // switching back to the shorter contact form leaves the container
  // exactly the same size. Kept in state (not a ref) so the min-height
  // is applied via `style` on every render.
  const [minFormHeight, setMinFormHeight] = useState(0);
  const formHeightObserverRef = useRef<ResizeObserver | null>(null);

  // Ref-callback pattern: React calls it with the fresh element every
  // time AnimatePresence mounts a new form, and with `null` on unmount.
  // We re-attach a ResizeObserver each time so we always measure the
  // currently-visible form (even if its own contents change, e.g. when
  // "Service Interested In" flips to "Other / Not Sure" and reveals an
  // extra input).
  const attachFormMeasurement = useCallback((el: HTMLDivElement | null) => {
    // Tear down the previous observer regardless.
    formHeightObserverRef.current?.disconnect();
    formHeightObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const measure = (h: number) => {
      // Round up to the nearest px so sub-pixel changes don't cause
      // pointless React re-renders.
      const ceil = Math.ceil(h);
      setMinFormHeight((prev) => (ceil > prev ? ceil : prev));
    };
    // Prime the value synchronously so the first paint after a tall
    // form mounts already carries the new min-height (no visible pop).
    measure(el.offsetHeight);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) measure(entry.contentRect.height);
    });
    ro.observe(el);
    formHeightObserverRef.current = ro;
  }, []);

  // Cleanup on unmount so we don't leak the observer if the page tears
  // down while a form is still mounted.
  useEffect(() => {
    return () => {
      formHeightObserverRef.current?.disconnect();
      formHeightObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const inquiry = searchParams.get('inquiry');
    const serviceParam = searchParams.get('service');

    if (inquiry === 'enterprise') {
      setFormData((prev) => ({
        ...prev,
        message:
          'I am interested in enterprise solutions for my organization. Please contact me to discuss our requirements.\n\n',
        service: 'other',
      }));
    }

    if (inquiry === 'support') {
      setFormData((prev) => ({
        ...prev,
        message: 'I need support with:\n\n',
        service: 'other',
      }));
    }

    if (inquiry === 'consultation') {
      setFormData((prev) => ({
        ...prev,
        message: 'I would like to schedule a consultation to discuss:\n\n',
        service: 'other',
      }));
    }

    // "Do your AI Readiness Assessment" CTA — sent from the
    // /services/artificial-intelligence detail page. Pre-fills a
    // starter message so the user only needs to add specifics; the
    // service pre-select below handles the dropdown separately.
    if (inquiry === 'ai-readiness') {
      setFormData((prev) => ({
        ...prev,
        message:
          "I'd like to do the AI Readiness Assessment. Please help me understand where my organization currently stands and what the recommended next steps are.\n\n",
        service: 'artificial-intelligence',
      }));
    }

    // "Industries We Empower" sidebar sub-items — each industry
    // tile routes here with `?inquiry=industry&industry=<slug>`.
    // We keep an inline slug→label table so the message reads with
    // the human name ("Healthcare and Life Sciences") rather than
    // the slug. Kept small on purpose — if this list grows past a
    // handful, move it into `src/data/` alongside `whatWeDo`.
    if (inquiry === 'industry') {
      const industryParam = searchParams.get('industry') ?? '';
      // Resolve the slug against the shared industry catalog so the
      // starter message names the industry the way the site does
      // everywhere else, and — crucially — pre-selects the Industry
      // dropdown on the form itself. If the slug isn't recognised
      // we still write a generic opener (useful signal for sales)
      // and leave the dropdown untouched.
      const matched = INDUSTRIES.find((ind) => ind.id === industryParam);
      const opener = matched
        ? `I'd like to talk about how Istatec works with teams in ${matched.title}. Here's a bit about where we are and what we're trying to solve:\n\n`
        : "I'd like to talk about how Istatec works with teams in my industry. Here's a bit about where we are and what we're trying to solve:\n\n";
      setFormData((prev) => ({
        ...prev,
        message: opener,
        service: 'other',
        industry: matched?.id ?? prev.industry,
      }));
    }

    // Generic pre-selection support — any /services/<slug> page's
    // "Connect with our experts" button routes here with
    // `?service=<slug>`. If the slug matches one of the dropdown
    // options, drop it into `service` so the user doesn't have to
    // pick it manually. The `inquiry` branches above (which set
    // `service: 'other'`) intentionally win over this — those flows
    // reach the contact form for a different reason.
    if (
      serviceParam &&
      SERVICE_OPTIONS.some((o) => o.id === serviceParam) &&
      inquiry !== 'enterprise' &&
      inquiry !== 'support' &&
      inquiry !== 'consultation' &&
      inquiry !== 'industry'
    ) {
      setFormData((prev) => ({ ...prev, service: serviceParam }));
    }
  }, [searchParams]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.relationship) {
      newErrors.relationship = 'Please select your relationship with us';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }

    if (
      formData.personalEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail)
    ) {
      newErrors.personalEmail = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (!formData.phone.number.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    } else if (formData.service === 'other' && !formData.customService.trim()) {
      newErrors.customService = 'Please describe the service you are looking for';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide more details (at least 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.workEmail.trim(),
        require_work_email: true,
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 400) {
        setErrors((prev) => ({
          ...prev,
          workEmail:
            response.message ||
            'Please use a work email address. Personal providers (gmail, hotmail, yahoo…) are not accepted.',
        }));
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
        if (retryAfter) {
          setApiError(`Please wait ${retryAfter} seconds before requesting another OTP.`);
        } else {
          setApiError(response.message);
        }
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerify = useCallback(
    async (otp: string) => {
      setOtpVerifyError('');
      setIsVerifyingOtp(true);

      try {
        const serviceName = resolveServiceLabel(formData);

        const response = await validateOtpAndSubmit({
          name: formData.name.trim(),
          work_email: formData.workEmail.trim(),
          personal_email: formData.personalEmail.trim() || 'na@gmail.com',
          company: formData.company.trim(),
          phone_number: composePhoneNumber(formData.phone),
          service_interested_in: serviceName,
          relationship: resolveRelationshipLabel(formData),
          industry: resolveIndustryLabel(formData.industry),
          message: formData.message.trim(),
          otp,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setSubmittedMode('contact');
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(response.message || 'Verification failed. Please try again.');
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    [formData],
  );

  const handleResendOtp = useCallback(async () => {
    const response = await sendOtp({
      name: formData.name.trim(),
      email: formData.workEmail.trim(),
      require_work_email: true,
    });

    if (response.status === 'success') {
      return { success: true };
    }
    if (response.status_code === 429) {
      const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
      setOtpVerifyError(response.message);
      return { success: false, retryAfter: retryAfter || 60 };
    }
    setOtpVerifyError(response.message || 'Failed to resend OTP.');
    return { success: false };
  }, [formData.name, formData.workEmail]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // If the user picks a preset service, forget any lingering custom text
      // so it can't accidentally be submitted the next time they flip to "other".
      if (name === 'service' && value !== 'other') {
        next.customService = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'service' && errors.customService) {
      setErrors((prev) => ({ ...prev, customService: '' }));
    }
    if (apiError) setApiError('');
  };

  const handlePhoneChange = (value: PhoneValue) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  // Setter passed to the MessageAssistToolbar so it can write dictated
  // transcripts + AI-polished replacements straight into the shared
  // `formData.message` slot. Kept as a stable callback because the
  // toolbar stashes it in a ref (so a fresh identity every render would
  // cause its cached ref to lag behind).
  const setMessageValue = useCallback((next: string) => {
    setFormData((prev) => ({ ...prev, message: next }));
    setErrors((prev) => (prev.message ? { ...prev, message: '' } : prev));
  }, []);

  // ---- Mode switch ---------------------------------------------------------
  // Called by the Contact us / Submit an RFP pills. Determines slide
  // direction from the tab order (contact = index 0, rfp = index 1) so the
  // AnimatePresence variants animate the incoming form in from the
  // "correct" side.
  const handleModeSwitch = (next: FormMode) => {
    if (next === formMode) return;
    slideDirRef.current = next === 'rfp' ? 1 : -1;
    setFormMode(next);
    // Wipe any residual field errors so switching tabs feels like a fresh
    // slate — but keep the shared field values (name / email / phone /
    // company / message) so the user isn't punished for exploring both
    // options.
    setErrors({});
    setApiError('');
  };

  // ---- RFP-only handlers ---------------------------------------------------
  const toggleRfpRelationship = (id: string) => {
    setRfpRelationships((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
    if (errors.rfpRelationships) {
      setErrors((prev) => ({ ...prev, rfpRelationships: '' }));
    }
  };

  const acceptRfpFile = (file: File | null) => {
    // We still commit the file even if validation fails so the user can
    // see what they picked; the error message tells them why it was
    // rejected. The submit handler blocks the actual request if the
    // file is invalid.
    setRfpFile(file);
    if (!file) return;
    const check = validateRfpDocument(file);
    setErrors((prev) => ({
      ...prev,
      rfpFile: check.ok ? '' : check.message,
    }));
  };

  const handleRfpFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    acceptRfpFile(file);
    // Allow re-selecting the same file after a remove-then-reselect flow.
    e.target.value = '';
  };

  const handleRfpFileDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file) acceptRfpFile(file);
  };

  const handleRemoveRfpFile = () => {
    setRfpFile(null);
    if (errors.rfpFile) {
      setErrors((prev) => ({ ...prev, rfpFile: '' }));
    }
  };

  const validateRfpForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (rfpRelationships.length === 0) {
      newErrors.rfpRelationships = 'Please select at least one relationship';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }
    if (
      formData.personalEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail)
    ) {
      newErrors.personalEmail = 'Please enter a valid email address';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    if (!formData.phone.number.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide more details (at least 10 characters)';
    }

    const fileCheck = validateRfpDocument(rfpFile);
    if (!fileCheck.ok) {
      newErrors.rfpFile = fileCheck.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resolveRfpRelationshipLabels = (): string => {
    if (rfpRelationships.length === 0) return 'NA';
    return rfpRelationships
      .map((id) => {
        const opt = RELATIONSHIP_OPTIONS.find((o) => o.id === id);
        return opt?.label ?? id;
      })
      .join(', ');
  };

  const handleRfpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateRfpForm()) return;

    setIsSubmitting(true);
    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.workEmail.trim(),
        require_work_email: true,
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 400) {
        setErrors((prev) => ({
          ...prev,
          workEmail:
            response.message ||
            'Please use a work email address. Personal providers (gmail, hotmail, yahoo…) are not accepted.',
        }));
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)
          ?.retry_after_seconds;
        if (retryAfter) {
          setApiError(`Please wait ${retryAfter} seconds before requesting another OTP.`);
        } else {
          setApiError(response.message);
        }
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Called by the OTP modal in RFP mode. Mirrors `handleOtpVerify` above
  // but routes through the multipart `submitRfp` endpoint (which needs
  // the PDF attachment in a `FormData` body).
  const handleRfpOtpVerify = useCallback(
    async (otp: string) => {
      setOtpVerifyError('');
      setIsVerifyingOtp(true);

      try {
        if (!rfpFile) {
          setOtpVerifyError('Missing RFP document. Please re-attach and try again.');
          return;
        }
        const response = await submitRfp({
          name: formData.name.trim(),
          work_email: formData.workEmail.trim(),
          personal_email: formData.personalEmail.trim() || 'na@gmail.com',
          company: formData.company.trim(),
          phone_number: composePhoneNumber(formData.phone),
          relationship: resolveRfpRelationshipLabels(),
          industry: resolveIndustryLabel(formData.industry),
          message: formData.message.trim(),
          otp,
          rfp_document: rfpFile,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setSubmittedMode('rfp');
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(response.message || 'Verification failed. Please try again.');
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, rfpFile, rfpRelationships],
  );

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData(INITIAL_FORM);
    setErrors({});
    setApiError('');
    setOtpVerifyError('');
    setRfpRelationships([...DEFAULT_RFP_RELATIONSHIP_IDS]);
    setRfpFile(null);
    setSubmittedMode('contact');
  };

  return (
    <>
      {/* OTP Verification Modal — the verify handler is chosen based on
          which form the user submitted, but the send/resend + email
          fields are identical across modes (both use `workEmail`). */}
      <OtpModal
        isOpen={showOtpModal}
        email={formData.workEmail}
        onClose={() => {
          setShowOtpModal(false);
          setOtpVerifyError('');
        }}
        onVerify={formMode === 'rfp' ? handleRfpOtpVerify : handleOtpVerify}
        onResend={handleResendOtp}
        verifyError={otpVerifyError}
        isVerifying={isVerifyingOtp}
      />

      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-white/10 text-white border border-white/20">
            Contact Us
          </Badge>
        }
        title="Let's Build Something Great Together"
        subtitle="Ready to start your project? Have questions about our services? We'd love to hear from you."
      />

      {/* Contact Form Section — full-bleed animated deep-purple background.
          The `<section>` is edge-to-edge; the animated orbs sit behind the
          content and slowly drift for a "living" feel. Inputs use a glassy
          treatment so they read well on the dark bg. */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          // Base colour is #051124 (HSL 217°, 76%, 8%) — a deep navy
          // blue. The four gradient stops are lightness variants of the
          // same hue so the diagonal wash gives the section depth
          // without shifting the colour off-brand:
          //   #020815 — L=4%  (darker corner)
          //   #051124 — L=8%  (base — the exact colour requested)
          //   #0a2148 — L=16% (brighter mid)
          //   #071730 — L=10% (darker corner)
          background:
            'linear-gradient(135deg, #020815 0%, #051124 35%, #0a2148 65%, #071730 100%)',
        }}
      >
        {/* Slowly-drifting orbs — the "moving background". Blue /
            indigo family so they complement the navy base (HSL 217°).
            Low opacity so they read as ambient light rather than a
            bright wash. */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-3xl bg-blue-600/25"
          animate={{ x: [0, 90, 0], y: [0, 70, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -bottom-48 -right-32 w-[600px] h-[600px] rounded-full blur-3xl bg-indigo-600/25"
          animate={{ x: [0, -80, 0], y: [0, -60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full blur-3xl bg-sky-600/15"
          animate={{ x: [-60, 60, -60], y: [40, -40, 40] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Soft top / bottom vignette so the section anchors visually against
            the neighbouring hero + purpose sections without a hard seam. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(1200px 500px at 50% 0%, rgba(0,0,0,0.35) 0%, transparent 70%), radial-gradient(1200px 500px at 50% 100%, rgba(0,0,0,0.35) 0%, transparent 70%)',
          }}
        />

        <Container className="relative z-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="heading-md text-white mb-4">Let's Connect</h2>
              <p className="text-base text-white/70 leading-relaxed mb-8">
                Stay ahead with the latest updates or kick off an exciting
                conversation with us today. Our team gets back to every inquiry
                within 24 hours.
              </p>

              <div className="space-y-6 mb-8">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 flex items-center justify-center group-hover:bg-white/20 group-hover:border-white/25 transition-all">
                    <Mail className="w-6 h-6 text-accent-300" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/50">Email</div>
                    <div className="text-white font-medium group-hover:text-accent-200 transition-colors">
                      {CONTACT_INFO.email}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-accent-300" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/50">Address</div>
                    <div className="text-white font-medium">
                      {CONTACT_INFO.address}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-accent-300" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/50">Business Hours</div>
                    <div className="text-white font-medium">
                      Mon - Fri: 9:00 AM - 6:00 PM IST
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <div className="text-xs uppercase tracking-wide text-white/50 mb-3">Follow Us</div>
                <div className="flex gap-3">
                  <a
                    href={SOCIAL_LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-accent-500 hover:text-white hover:border-accent-500 transition-all"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={SOCIAL_LINKS.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-accent-500 hover:text-white hover:border-accent-500 transition-all"
                    aria-label="X (Twitter)"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={SOCIAL_LINKS.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-accent-500 hover:text-white hover:border-accent-500 transition-all"
                    aria-label="YouTube"
                  >
                    <Youtube className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form area — houses two swappable forms (Contact us
                and Submit an RFP). The pill tab switcher above the card
                controls which form is visible, and the swap is animated
                with a direction-aware slide via AnimatePresence. */}
            <div className="lg:col-span-3">
              {/* Tab switcher (Contact us / Submit an RFP) — kept outside
                  AnimatePresence so it stays put while the forms slide.
                  Hidden on the success screen so the user gets a clean
                  confirmation view. */}
              {!isSubmitted && (
                <div className="flex flex-wrap gap-3 mb-6">
                  {(
                    [
                      { id: 'contact' as const, label: 'Contact us' },
                      { id: 'rfp' as const, label: 'Submit an RFP' },
                    ]
                  ).map((tab) => {
                    const active = formMode === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => handleModeSwitch(tab.id)}
                        aria-pressed={active}
                        className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                          active
                            ? 'bg-white text-navy-950 border-white shadow-lg'
                            : 'bg-white/5 text-white/85 border-white/25 hover:bg-white/10 hover:border-white/40'
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* The card is wrapped in a clipping container so the
                  incoming form doesn't visually escape while sliding in
                  from the right / left. */}
              <div className="relative">
                {/* Success view — mode-agnostic banner. Rendered outside
                    the AnimatePresence tab-swap so switching tabs on the
                    success screen (which is disabled anyway) can't ever
                    slide the confirmation off-screen. */}
                {isSubmitted ? (
                  // Solid-white success card so the confirmation reads
                  // crisply against the deep-navy section behind it.
                  <Card padding="lg" className="bg-white border border-navy-100 shadow-2xl">
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-navy-900 mb-2">
                        {submittedMode === 'rfp' ? 'RFP received!' : 'Thank You!'}
                      </h3>
                      <p className="text-navy-600 mb-6">
                        {submittedMode === 'rfp'
                          ? "We've got your RFP and the attached document. Our team will review it and get back to you within 24 hours."
                          : 'Your inquiry has been submitted successfully. Our team will get back to you within 24 hours.'}
                      </p>
                      <Button
                        variant="outline"
                        onClick={resetForm}
                        className="border-navy-300 text-navy-900 hover:bg-navy-50"
                      >
                        Send Another Message
                      </Button>
                    </motion.div>
                  </Card>
                ) : (
                  // Shared class strings — glassy inputs on the purple bg.
                  // Both the contact and RFP forms consume these so every
                  // input reads the same on the dark bg.
                  (() => {
                    // Shared class strings — solid-white inputs so the
                    // form Card reads crisp and clearly editable against
                    // the deep-navy section behind it. Every input pulls
                    // from these two helpers so a single tweak here
                    // propagates everywhere.
                    const baseInput =
                      'w-full px-4 py-3 rounded-lg border bg-white text-navy-900 placeholder:text-navy-400 outline-none transition-all focus:ring-2';
                    const okInput =
                      'border-navy-200 hover:border-navy-300 focus:border-accent-500 focus:ring-accent-400/30';
                    const errInput =
                      'border-red-400 focus:border-red-500 focus:ring-red-400/30';
                    const labelCls = 'block text-sm font-medium text-navy-800 mb-2';
                    const errCls = 'mt-1 text-sm text-red-600 flex items-center gap-1';
                    const helpCls = 'mt-1 text-xs text-navy-500';
                    const inputCls = (err?: string) =>
                      `${baseInput} ${err ? errInput : okInput}`;
                    // <option> renders on the OS layer — keep it white
                    // so the dropdown menu matches the input surface.
                    const optionCls = 'bg-white text-navy-900';

                    // Direction-aware slide variants for AnimatePresence.
                    // `custom` is set at swap time via `slideDirRef` so the
                    // incoming form enters from the appropriate side.
                    const slideVariants = {
                      enter: (dir: 1 | -1) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
                      center: { x: 0, opacity: 1 },
                      exit: (dir: 1 | -1) => ({ x: dir > 0 ? '-30%' : '30%', opacity: 0 }),
                    } as const;

                    return (
                <div
                  className="relative overflow-hidden"
                  // Pinned to the tallest form's height (measured via
                  // ResizeObserver, grow-only). Prevents the surrounding
                  // purple section from jumping when the user toggles
                  // between the Contact us and Submit an RFP tabs.
                  style={
                    minFormHeight ? { minHeight: `${minFormHeight}px` } : undefined
                  }
                >
                  <AnimatePresence mode="wait" custom={slideDirRef.current} initial={false}>
                    <motion.div
                      key={formMode}
                      ref={attachFormMeasurement}
                      custom={slideDirRef.current}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Card
                        padding="lg"
                        className="bg-white border border-navy-100 shadow-2xl"
                      >
                        {/* Small heading inside the card so users always
                            know which flow they're on — mirrors the
                            reference design (Send us a message / Submit
                            a request for proposal). */}
                        <div className="mb-6">
                          <h3 className="text-2xl md:text-3xl font-bold text-navy-900">
                            {formMode === 'rfp'
                              ? 'Submit a request for proposal'
                              : 'Send us a message'}
                          </h3>
                          <p className="mt-1 text-sm text-navy-600">
                            {formMode === 'rfp'
                              ? 'Share your requirements and attach your RFP/RFI document — we typically respond within 24 hours.'
                              : "Tell us what you're building; we'll be in touch within 24 hours."}
                          </p>
                        </div>

                        {formMode === 'contact' ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {apiError && (
                      <motion.div
                        className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-700">{apiError}</p>
                      </motion.div>
                    )}

                    {/* Relationship dropdown — kept at the top of the form so
                        it filters the intent of the inquiry before anything else. */}
                    <div>
                      <label htmlFor="relationship" className={labelCls}>
                        What's your relationship with us? *
                      </label>
                      <select
                        id="relationship"
                        name="relationship"
                        value={formData.relationship}
                        onChange={handleChange}
                        className={inputCls(errors.relationship)}
                      >
                        <option value="" className={optionCls}>
                          Select an option
                        </option>
                        {RELATIONSHIP_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id} className={optionCls}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.relationship && (
                        <p className={errCls}>
                          <AlertCircle className="w-3 h-3" />
                          {errors.relationship}
                        </p>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className={labelCls}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={inputCls(errors.name)}
                          placeholder="Aarav Sharma"
                        />
                        {errors.name && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="company" className={labelCls}>
                          Company *
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className={inputCls(errors.company)}
                          placeholder="Company Inc."
                        />
                        {errors.company && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.company}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="workEmail" className={labelCls}>
                          Work Email *
                        </label>
                        <input
                          type="email"
                          id="workEmail"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleChange}
                          className={inputCls(errors.workEmail)}
                          placeholder="you@company.com"
                        />
                        <p className={helpCls}>
                          We'll send a verification code here. Personal domains (gmail, yahoo…) are
                          not accepted.
                        </p>
                        {errors.workEmail && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.workEmail}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="personalEmail" className={labelCls}>
                          Personal Email{' '}
                          <span className="text-navy-500 font-normal">(optional)</span>
                        </label>
                        <input
                          type="email"
                          id="personalEmail"
                          name="personalEmail"
                          value={formData.personalEmail}
                          onChange={handleChange}
                          className={inputCls(errors.personalEmail)}
                          placeholder="you@gmail.com"
                        />
                        {errors.personalEmail && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.personalEmail}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className={labelCls}>
                          Phone Number *
                        </label>
                        <PhoneInput
                          id="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          hasError={Boolean(errors.phone)}
                        />
                        {errors.phone && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.phone}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="service" className={labelCls}>
                          Service Interested In *
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className={inputCls(errors.service)}
                        >
                          <option value="" className={optionCls}>
                            Select a service
                          </option>
                          {SERVICE_OPTIONS.map((service) => (
                            <option key={service.id} value={service.id} className={optionCls}>
                              {service.title}
                            </option>
                          ))}
                          <option value="other" className={optionCls}>Other / Not Sure</option>
                        </select>
                        {errors.service && (
                          <p className={errCls}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.service}
                          </p>
                        )}
                        {formData.service === 'other' && (
                          <div className="mt-2">
                            <input
                              type="text"
                              id="customService"
                              name="customService"
                              value={formData.customService}
                              onChange={handleChange}
                              autoFocus
                              className={inputCls(errors.customService)}
                              placeholder="Please describe the service you're looking for"
                            />
                            {errors.customService && (
                              <p className={errCls}>
                                <AlertCircle className="w-3 h-3" />
                                {errors.customService}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Industry dropdown — optional. Sits on its own row
                        between Phone/Service and Message so it reads as
                        an additional categorization field, not a
                        contact detail. Options are derived from the
                        shared `INDUSTRY_OPTIONS` (which is itself
                        derived from `src/data/industries.ts`) so the
                        sidebar, industry landing pages, and this
                        dropdown always stay in lock-step. */}
                    <div>
                      <label htmlFor="industry" className={labelCls}>
                        Industry{' '}
                        <span className="text-navy-500 font-normal">(optional)</span>
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className={inputCls(errors.industry)}
                      >
                        <option value="" className={optionCls}>
                          Select your industry
                        </option>
                        {INDUSTRY_OPTIONS.map((ind) => (
                          <option key={ind.id} value={ind.id} className={optionCls}>
                            {ind.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className={labelCls}>
                        Message *
                      </label>
                      {/* Mic (dictate) + Sparkles (AI polish) icons live
                          inside the textarea's top-right corner. Toolbar
                          wraps the textarea and pins the two circular
                          buttons over its top-right corner; the reserved
                          right-padding on the textarea keeps typed text
                          from sliding under them. */}
                      <MessageAssistToolbar
                        value={formData.message}
                        onChange={setMessageValue}
                        context="contact"
                        disabled={isSubmitting}
                      >
                        <textarea
                          id="message"
                          name="message"
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          className={`${inputCls(errors.message)} resize-none`}
                          // ~96px of right padding = two 36px circles +
                          // 6px gap + 10px inset + a little breathing
                          // room. Inline style so it beats the shared
                          // `px-4` on baseInput without an !important.
                          style={{ paddingRight: '6rem' }}
                          placeholder="Tell us about your project or requirements..."
                        />
                      </MessageAssistToolbar>
                      {errors.message && (
                        <p className={errCls}>
                          <AlertCircle className="w-3 h-3" />
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      isLoading={isSubmitting}
                      rightIcon={<Send className="w-5 h-5" />}
                    >
                      Send Message
                    </Button>

                    <p className="text-xs text-navy-500 text-center">
                      We'll send a 4-digit verification code to your work email to confirm your
                      identity.
                    </p>

                    {/* Privacy / compliance reassurance — same wording
                        as every other Let's Connect form on the site,
                        centralised in `<FormPrivacyNotice />` so any
                        legal / tone edit ships everywhere at once. */}
                    <FormPrivacyNotice tone="onLight" className="mt-2" />
                  </form>
                        ) : (
                          /* ==================== RFP FORM ====================
                              Same shared field set as the contact form, plus
                              a required PDF (≤5 MB) attachment. Relationship
                              is a multi-select chip group instead of a
                              dropdown, defaulted to Client/Prospect +
                              Partner/Alliance per the reference UI. */
                          <form onSubmit={handleRfpSubmit} className="space-y-6">
                            {apiError && (
                              <motion.div
                                className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                              >
                                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                <p className="text-sm text-red-700">{apiError}</p>
                              </motion.div>
                            )}

                            {/* Relationship (multi-select chips). At least
                                one must be selected — the two defaults are
                                pre-populated on mount. */}
                            <div>
                              <label className={labelCls}>
                                What's your relationship with us? *
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {RELATIONSHIP_OPTIONS.map((opt) => {
                                  const selected = rfpRelationships.includes(opt.id);
                                  return (
                                    <button
                                      key={opt.id}
                                      type="button"
                                      onClick={() => toggleRfpRelationship(opt.id)}
                                      aria-pressed={selected}
                                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-all ${
                                        selected
                                          ? 'bg-accent-500 text-white border-accent-500 shadow-md'
                                          : 'bg-white text-navy-700 border-navy-200 hover:bg-navy-50 hover:border-navy-300'
                                      }`}
                                    >
                                      <span>{opt.label}</span>
                                      {selected && (
                                        <XIcon
                                          className="w-3.5 h-3.5 opacity-80"
                                          aria-hidden
                                        />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className={helpCls}>
                                Tap chips to add or remove — you can pick more than one.
                              </p>
                              {errors.rfpRelationships && (
                                <p className={errCls}>
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.rfpRelationships}
                                </p>
                              )}
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label htmlFor="rfp-name" className={labelCls}>
                                  Full Name *
                                </label>
                                <input
                                  type="text"
                                  id="rfp-name"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleChange}
                                  className={inputCls(errors.name)}
                                  placeholder="Aarav Sharma"
                                />
                                {errors.name && (
                                  <p className={errCls}>
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.name}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label htmlFor="rfp-company" className={labelCls}>
                                  Company *
                                </label>
                                <input
                                  type="text"
                                  id="rfp-company"
                                  name="company"
                                  value={formData.company}
                                  onChange={handleChange}
                                  className={inputCls(errors.company)}
                                  placeholder="Company Inc."
                                />
                                {errors.company && (
                                  <p className={errCls}>
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.company}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label htmlFor="rfp-workEmail" className={labelCls}>
                                  Work Email *
                                </label>
                                <input
                                  type="email"
                                  id="rfp-workEmail"
                                  name="workEmail"
                                  value={formData.workEmail}
                                  onChange={handleChange}
                                  className={inputCls(errors.workEmail)}
                                  placeholder="you@company.com"
                                />
                                <p className={helpCls}>
                                  We'll send a verification code here. Personal domains (gmail, yahoo…) are
                                  not accepted.
                                </p>
                                {errors.workEmail && (
                                  <p className={errCls}>
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.workEmail}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label htmlFor="rfp-personalEmail" className={labelCls}>
                                  Personal Email{' '}
                                  <span className="text-navy-500 font-normal">(optional)</span>
                                </label>
                                <input
                                  type="email"
                                  id="rfp-personalEmail"
                                  name="personalEmail"
                                  value={formData.personalEmail}
                                  onChange={handleChange}
                                  className={inputCls(errors.personalEmail)}
                                  placeholder="you@gmail.com"
                                />
                                {errors.personalEmail && (
                                  <p className={errCls}>
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.personalEmail}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label htmlFor="rfp-phone" className={labelCls}>
                                Phone Number *
                              </label>
                              <PhoneInput
                                id="rfp-phone"
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                hasError={Boolean(errors.phone)}
                              />
                              {errors.phone && (
                                <p className={errCls}>
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.phone}
                                </p>
                              )}
                            </div>

                            {/* Industry — sits between Phone and the
                                requirement summary so it reads as the
                                last piece of categorisation before the
                                free-form message. Same option set as
                                the Contact-us tab. */}
                            <div>
                              <label htmlFor="rfp-industry" className={labelCls}>
                                Industry{' '}
                                <span className="text-navy-500 font-normal">(optional)</span>
                              </label>
                              <select
                                id="rfp-industry"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                className={inputCls(errors.industry)}
                              >
                                <option value="" className={optionCls}>
                                  Select your industry
                                </option>
                                {INDUSTRY_OPTIONS.map((ind) => (
                                  <option key={ind.id} value={ind.id} className={optionCls}>
                                    {ind.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label htmlFor="rfp-message" className={labelCls}>
                                Tell us briefly about your requirement *
                              </label>
                              {/* Same in-textarea dictate + AI-polish
                                  icons as the contact-us textarea, but
                                  tell the API this text came from the
                                  RFP tab so the polish reads slightly
                                  more crisp. */}
                              <MessageAssistToolbar
                                value={formData.message}
                                onChange={setMessageValue}
                                context="rfp"
                                disabled={isSubmitting}
                              >
                                <textarea
                                  id="rfp-message"
                                  name="message"
                                  rows={5}
                                  value={formData.message}
                                  onChange={handleChange}
                                  className={`${inputCls(errors.message)} resize-none`}
                                  style={{ paddingRight: '6rem' }}
                                  placeholder="Summarize the scope, timelines, and any specific asks…"
                                />
                              </MessageAssistToolbar>
                              {errors.message && (
                                <p className={errCls}>
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.message}
                                </p>
                              )}
                            </div>

                            {/* PDF uploader — drag-drop OR click to browse.
                                Client-side validation runs on every accept
                                so the user gets an instant error before
                                the request is fired. */}
                            <div>
                              <label className={labelCls}>
                                Upload RFP / RFI document *{' '}
                                <span className="text-navy-500 font-normal">
                                  (PDF only · max 5 MB)
                                </span>
                              </label>
                              {rfpFile ? (
                                <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-navy-200 bg-navy-50">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <FileText className="w-5 h-5 text-accent-600 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-navy-900 truncate">
                                        {rfpFile.name}
                                      </p>
                                      <p className="text-xs text-navy-500">
                                        {(rfpFile.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleRemoveRfpFile}
                                    className="p-1.5 rounded-md text-navy-500 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                    aria-label="Remove file"
                                  >
                                    <XIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <label
                                  htmlFor="rfp-attachment"
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDraggingFile(true);
                                  }}
                                  onDragLeave={() => setIsDraggingFile(false)}
                                  onDrop={handleRfpFileDrop}
                                  className={`flex items-center gap-3 px-4 py-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                                    errors.rfpFile
                                      ? 'border-red-300 bg-red-50'
                                      : isDraggingFile
                                        ? 'border-accent-400 bg-accent-50'
                                        : 'border-navy-200 bg-navy-50 hover:bg-navy-100 hover:border-navy-300'
                                  }`}
                                >
                                  <Paperclip className="w-5 h-5 text-navy-500 flex-shrink-0" />
                                  <div className="text-sm text-navy-700">
                                    Drag and drop your file here or{' '}
                                    <span className="font-semibold text-accent-600 underline underline-offset-2">
                                      Browse
                                    </span>
                                    <span className="block text-xs text-navy-500 mt-0.5">
                                      PDF only · up to{' '}
                                      {(MAX_RFP_BYTES / (1024 * 1024)).toFixed(0)} MB
                                    </span>
                                  </div>
                                  <input
                                    id="rfp-attachment"
                                    type="file"
                                    accept={RFP_ACCEPT_ATTR}
                                    onChange={handleRfpFileInput}
                                    className="hidden"
                                  />
                                </label>
                              )}
                              {errors.rfpFile && (
                                <p className={errCls}>
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.rfpFile}
                                </p>
                              )}
                            </div>

                            <Button
                              type="submit"
                              variant="primary"
                              size="lg"
                              className="w-full"
                              isLoading={isSubmitting}
                              rightIcon={<Upload className="w-5 h-5" />}
                            >
                              Submit RFP
                            </Button>

                            <p className="text-xs text-navy-500 text-center">
                              We'll send a 4-digit verification code to your work email to confirm your
                              identity.
                            </p>

                            {/* Privacy / compliance reassurance — same
                                wording as the Contact-us tab and the
                                global ConnectSection. Anchored at the
                                bottom so it's the last thing a visitor
                                reads before submitting the RFP. */}
                            <FormPrivacyNotice tone="onLight" className="mt-2" />
                          </form>
                        )}
                      </Card>
                    </motion.div>
                  </AnimatePresence>
                </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ═════════════════ Why teams choose Istatec ═════════════════
          Placed AFTER the form so a visitor who's already filled it
          out reads the reasoning as a "here's what you just signed
          up for" reassurance, and a visitor who scrolled past the
          form still gets the pitch before hitting the alternative
          inboxes / OurPurpose sections below. Same component that
          the About page uses so the messaging stays in one place. */}
      <WhyChooseUsSection />

      {/* Function-specific email directory — sits below the form
          so a visitor whose inquiry belongs in a different inbox
          (careers, investors, media) can bypass the form and mail
          the right team directly. Full-bleed black to visually break
          from the purple form section above and the "Our Purpose"
          section below. */}
      <section className="bg-navy-950 border-t border-white/5">
        <Container className="py-14 md:py-20">
          <h2 className="heading-lg text-white mb-10 md:mb-12">
            Looking for a specific function?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
            {(
              [
                { label: 'Sales / Service', email: 'info@istatec.com' },
                { label: 'Investors', email: 'investors@istatec.com' },
                { label: 'Media Relations', email: 'media@istatec.com' },
                { label: 'Careers', email: 'careers@istatec.com' },
              ]
            ).map((entry) => (
              <div key={entry.label} className="flex flex-col">
                <span className="text-sm text-white/55 tracking-wide">
                  {entry.label}
                </span>
                <a
                  href={`mailto:${entry.email}`}
                  className="mt-2 text-lg md:text-xl font-semibold text-white underline decoration-transparent underline-offset-4 hover:decoration-accent-300 hover:text-accent-200 transition-colors break-all"
                >
                  {entry.email}
                </a>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Our Purpose — sits BELOW the contact form so visitors land on the
          form first, then get the mission statement as they continue reading. */}
      <OurPurpose />

      {/* Vision cover — sits between the Our-Purpose section and FAQ */}
      <section className="relative overflow-hidden bg-navy-950">
        {/* Background video */}
        <video
          src={visionCover}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Readability + brand overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-navy-950/75 via-navy-950/55 to-navy-950/85"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(900px 400px at 20% 20%, rgba(26,110,255,0.18) 0%, transparent 60%), radial-gradient(700px 320px at 85% 85%, rgba(107,162,255,0.14) 0%, transparent 60%)',
          }}
        />

        <Container className="relative z-10 py-24 md:py-32 lg:py-40">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <Badge
              variant="accent"
              className="mb-6 bg-white/10 text-white border border-white/20 backdrop-blur-sm"
            >
              Let's build together
            </Badge>

            <h2 className="heading-xl text-white leading-tight drop-shadow-lg">
              Let's connect and{' '}
              <span className="relative inline">
                <span className="relative z-10 bg-gradient-to-r from-accent-300 via-accent-400 to-accent-300 bg-clip-text text-transparent">
                  spark the future
                </span>
                <motion.span
                  aria-hidden
                  className="absolute -inset-1 rounded-lg bg-accent-500/20 blur-lg"
                  animate={{ opacity: [0.4, 0.75, 0.4] }}
                  transition={{ duration: 3.2, repeat: Infinity }}
                />
              </span>
              , together.
            </h2>

            <p className="mt-6 text-lg md:text-xl text-navy-100 leading-relaxed max-w-2xl mx-auto drop-shadow">
              Join the vision — and let's shape what's next, side by side.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <Section background="mesh" padding="lg">
        <Container size="md">
          <div className="text-center mb-12">
            <Badge variant="accent" className="mb-4">
              FAQ
            </Badge>
            <h2 className="heading-lg text-navy-900">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What is your typical response time?',
                a: 'We respond to all inquiries within 24 hours during business days. For urgent matters, please reach us via email.',
              },
              {
                q: 'Do you offer free consultations?',
                a: 'Yes! We offer a free initial consultation to understand your requirements and discuss how we can help.',
              },
              {
                q: 'What industries do you serve?',
                a: 'We serve clients across various industries including fintech, healthcare, e-commerce, education, and enterprise software.',
              },
              {
                q: 'Can you work with our existing team?',
                a: "Absolutely. We offer staff augmentation services where our experts integrate seamlessly with your team's workflow and processes.",
              },
            ].map((faq, index) => (
              <Card key={index} className="text-left">
                <h3 className="text-lg font-semibold text-navy-900 mb-2">{faq.q}</h3>
                <p className="text-navy-600">{faq.a}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
};
